import {useState} from "react";
import {addDays, startOfToday, format} from "date-fns";
import {ja} from "react-day-picker/locale";

import {Sheet, SheetContent, SheetHeader, SheetTitle} from "./sheet";
import {Calendar} from "./calendar";
import {buttonVariants} from "./button";
import {cn} from "../lib/utils";

interface DatePickerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: Date | undefined;
  onChange: (day: Date | undefined) => void;
  title?: string;
}

const PRESETS = [
  {label: "今日", getValue: () => startOfToday()},
  {label: "明日", getValue: () => addDays(startOfToday(), 1)},
] as const;

function DatePickerSheet({open, onOpenChange, value, onChange, title = "日付を選択"}: DatePickerSheetProps) {
  const [month, setMonth] = useState<Date>(value ?? new Date());

  const handleOpenChange = (next: boolean) => {
    if (next) setMonth(value ?? new Date());
    onOpenChange(next);
  };

  const select = (day: Date | undefined) => {
    onChange(day);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl p-0 max-h-[70vh]">
        <SheetHeader className="border-b px-4 pt-4 pb-3">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-base font-bold">{title}</SheetTitle>
            {value && (
              <span className="text-sm text-muted-foreground">{format(value, "yyyy/MM/dd")}</span>
            )}
          </div>
        </SheetHeader>

        <div className="space-y-4 px-4 py-4">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                className={cn(buttonVariants({variant: "outline"}), "h-11 px-4 text-sm")}
                onClick={() => select(preset.getValue())}
              >
                {preset.label}
              </button>
            ))}
            <button
              type="button"
              className={cn(buttonVariants({variant: "ghost"}), "h-11 px-4 text-sm text-muted-foreground")}
              onClick={() => select(undefined)}
            >
              クリア
            </button>
          </div>

          <div className="flex justify-center rounded-lg border bg-background p-2">
            <Calendar
              mode="single"
              selected={value}
              onSelect={(day) => {
                if (day) select(day);
              }}
              month={month}
              onMonthChange={setMonth}
              numberOfMonths={1}
              locale={ja}
              className="w-full"
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export {DatePickerSheet};
export type {DatePickerSheetProps};
